import React from 'react'
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { AppForm, FormCheckbox, FormSubmitButton, FormTextField } from '../../components/FormCore'
import { CancelButton } from '../../components/CancelButton'
import { useZodForm } from '../../hooks/zod-form'
import { ShowDialogProps } from '../../show-dialog'
import { IROVVideoCategory, IROVVideoCategoryUpdate } from '../../generated-types/rovvideo'
import { invalidatePositionCache } from 'src/api/positions'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { toast } from 'react-toastify'
import { useUpdateROVCategory } from 'src/api/rov-category'

interface UpdateROVCategoryDialogProps extends ShowDialogProps {
  category: IROVVideoCategory
  customerId: EntityId
  localityId: EntityId
  mooringId: EntityId
  positionId: EntityId
  mainComponentId: EntityId
}

const UpdateROVCategoryDialog: React.FC<UpdateROVCategoryDialogProps> = ({
  pop,
  category,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
}) => {
  const updateROVCategory = useUpdateROVCategory(customerId, localityId, category.id)

  const { t } = useTranslation(['rov', 'common'])

  const schema = z.object({
    categoryName: z.string().nonempty({ message: t('categoryNameRequired', { ns: 'common' }) }),
    isShared: z.boolean(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      categoryName: category.categoryName,
      isShared: category.isShared,
    },
  })

  const onSubmit = async (formData: z.infer<typeof schema>) => {
    const updateData: IROVVideoCategoryUpdate = {
      categoryName: formData.categoryName,
      isShared: formData.isShared,
    }

    updateROVCategory.mutateAsync(updateData).then(() => {
      invalidatePositionCache.getPositionROVVideos(customerId, localityId, mooringId, undefined)
      invalidateMainComponentCache.getMainComponentROVVideos(customerId, undefined)
      toast.success(t('updatedSuccessfully', { ns: 'common' }))
    })
    pop(true)
  }

  return (
    <AppForm form={form} onSubmit={onSubmit} hasInitialValues={true}>
      <FormTextField fullWidth name="categoryName" label={t('categoryName')} placeholder={t('enterCategoryName')} />
      <Box py={1}>
        <FormCheckbox name="isShared" label={t('share', { ns: 'common' })} />
      </Box>
      <Box className="flex justify-end gap-4 pt-4">
        <FormSubmitButton createText={t('update', { ns: 'common' })} />
        <CancelButton onClick={() => pop()}>{t('cancel', { ns: 'common' })}</CancelButton>
      </Box>
    </AppForm>
  )
}

export default UpdateROVCategoryDialog
